import gql from 'graphql-tag';
import { graphQuery } from '@services/commService';
import { TableRequest, TableResponse } from '@components/Table';
import { UsersTableOptions, UsersTableRow } from './types';

const USERS_TABLE_QUERY = gql`
  query UsersTable(
    $page: Int!
    $pageSize: Int!
    $sortBy: UsersTableSortField!
    $sortOrder: SortOrderEnumType!
    $filters: UsersTableFilters
  ) {
    usersTable(
      page: $page
      pageSize: $pageSize
      sortBy: $sortBy
      sortOrder: $sortOrder
      filters: $filters
    ) {
      items {
        id
        firstName
        lastName
        email
        phone
        age
        businessUnits { id name }
        accountStatus
        mfaStatus
        mentors { id name }
        course { id name }
        skill { id name }
        lastPracticedAt
        alerts {
          id
          alertType
        }
      }
      totalItems
      page
      pageSize
    }
  }
`;

const USERS_TABLE_OPTIONS_QUERY = gql`
  query UsersTableOptions {
    usersTableOptions {
      businessUnits { id name }
      mentors { id name }
    }
  }
`;

const getStringArray = (value: unknown) => (Array.isArray(value) ? value : undefined);

const USERS_TABLE_SORT_FIELDS = new Set(['NAME', 'LAST_PRACTICED']);

export const getUsersTable = async (
  request: TableRequest,
): Promise<TableResponse<UsersTableRow>> => {
  const requestedSort = request.sort?.column;
  const response = await graphQuery(USERS_TABLE_QUERY, false, {
    page: request.page,
    pageSize: request.pageSize,
    sortBy: requestedSort && USERS_TABLE_SORT_FIELDS.has(requestedSort) ? requestedSort : 'NAME',
    sortOrder: request.sort?.direction ?? 'ASC',
    filters: {
      name: typeof request.filters.name === 'string' ? request.filters.name : undefined,
      businessUnitIds: getStringArray(request.filters.businessUnitIds),
      accountStatuses: getStringArray(request.filters.accountStatuses),
      mentorIds: getStringArray(request.filters.mentorIds),
      alertTypes: getStringArray(request.filters.alertTypes),
    },
  }, request.signal);

  if (!response?.data?.usersTable) {
    throw new Error('Users Table request failed.');
  }
  return response.data.usersTable;
};

export const getUsersTableOptions = async (signal?: AbortSignal): Promise<UsersTableOptions> => {
  const response = await graphQuery(USERS_TABLE_OPTIONS_QUERY, false, {}, signal);
  return response?.data?.usersTableOptions ?? {
    businessUnits: [],
    mentors: [],
  };
};